import React from 'react';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import { Linkedin, Twitter, Instagram, Facebook, ArrowUpRight, MapPin, Phone, Mail } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const FT = {
  en: {
    tagline: 'AI-powered marketing & sales engine for financial institutions. Attraction, conversion and activation — handled.',
    cta: "Let's connect",
    addressLabel: 'Address',
    contactLabel: 'Contact',
    quickLabel: 'Quick links',
    legalLabel: 'Legal',
    quick: [['/', 'Home'], ['/about', 'About Us'], ['/services', 'Services'], ['/pricing', 'Pricing'], ['/security', 'Security'], ['/blog', 'Blog'], ['/contact', 'Contact']],
    legal: [['/terms', 'Terms & Conditions'], ['/privacy', 'Privacy Policy'], ['/cookies', 'Cookie Policy']],
    rights: '© 2025 ClicksyHub LLC. All rights reserved.',
    appLabel: 'Use the platform',
  },
  es: {
    tagline: 'Motor de marketing y ventas con IA para instituciones financieras. Atracción, conversión y activación — resueltas.',
    cta: 'Conversemos',
    addressLabel: 'Dirección',
    contactLabel: 'Contacto',
    quickLabel: 'Enlaces rápidos',
    legalLabel: 'Legal',
    quick: [['/', 'Inicio'], ['/about', 'Nosotros'], ['/services', 'Servicios'], ['/pricing', 'Precios'], ['/security', 'Seguridad'], ['/blog', 'Blog'], ['/contact', 'Contacto']],
    legal: [['/terms', 'Términos y Condiciones'], ['/privacy', 'Política de Privacidad'], ['/cookies', 'Política de Cookies']],
    rights: '© 2025 ClicksyHub LLC. Todos los derechos reservados.',
    appLabel: 'Usar la plataforma',
  },
};

export default function Footer() {
  const { lang } = useLanguage();
  const t = FT[lang] || FT.en;
  return (
    <footer className="mt-32 border-t border-[#3A332A] grain">
      <div className="container-x py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Logo />
            <p className="mt-7 text-[#C6C4CF] text-[16px] leading-relaxed max-w-md">{t.tagline}</p>
            <Link to="/contact" className="btn-outline mt-8"><span>{t.cta}</span><span className="arrow"><ArrowUpRight size={14} /></span></Link>
          </div>
          <div className="lg:col-span-3">
            <p className="text-[#C6C4CF] text-xs uppercase tracking-[0.22em] font-medium pb-3 border-b border-[#3A332A]">{t.contactLabel}</p>
            <ul className="mt-5 space-y-4 text-[#C6C4CF]">
              <li className="flex gap-3"><MapPin size={16} className="mt-1 text-[#C6C4CF] flex-shrink-0" /><span>150 South Pines Island Road, Plantation, Florida 33324, USA</span></li>
              <li className="flex gap-3"><Phone size={16} className="mt-1 text-[#C6C4CF] flex-shrink-0" /><a href="tel:+19542785634" className="hover:text-[#FAF6EF]">+1 (954) 278-5634</a></li>
              <li className="flex gap-3"><Mail size={16} className="mt-1 text-[#C6C4CF] flex-shrink-0" /><a href="mailto:info@clicksyhub.com" className="hover:text-[#FAF6EF]">info@clicksyhub.com</a></li>
            </ul>
            <a href="https://app.clicksyhub.com" target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 text-[#C6C4CF] hover:text-[#FAF6EF] text-sm uppercase tracking-[0.22em]">{t.appLabel} <ArrowUpRight size={14} /></a>
          </div>
          <div className="lg:col-span-2">
            <p className="text-[#C6C4CF] text-xs uppercase tracking-[0.22em] font-medium pb-3 border-b border-[#3A332A]">{t.quickLabel}</p>
            <ul className="mt-5 space-y-3">
              {t.quick.map(([to, label]) => <li key={to}><Link to={to} className="text-[#C6C4CF] hover:text-[#FAF6EF] transition-colors">{label}</Link></li>)}
            </ul>
          </div>
          <div className="lg:col-span-2">
            <p className="text-[#C6C4CF] text-xs uppercase tracking-[0.22em] font-medium pb-3 border-b border-[#3A332A]">{t.legalLabel}</p>
            <ul className="mt-5 space-y-3">
              {t.legal.map(([to, label]) => <li key={to}><Link to={to} className="text-[#C6C4CF] hover:text-[#FAF6EF] transition-colors">{label}</Link></li>)}
            </ul>
          </div>
        </div>
        <div className="mt-16 pt-6 border-t border-[#3A332A] flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <p className="text-[#8A8275] text-sm">{t.rights}</p>
          <div className="flex items-center gap-3">
            {[Linkedin, Twitter, Instagram, Facebook].map((Icon, i) => (
              <a key={i} href="#" className="w-10 h-10 rounded-full border border-[#3A332A] flex items-center justify-center text-[#C6C4CF] hover:bg-[#F5F137] hover:text-[#232323] hover:border-[#F5F137] transition-colors"><Icon size={14} /></a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
